import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

export interface SpotlightItem {
  id: string;
  label: string;
  description?: string;
  icon?: string;
  action?: () => void;
}

interface SpotlightProps {
  open: boolean;
  onClose: () => void;
  items?: SpotlightItem[];
  placeholder?: string;
  className?: string;
  style?: CSSProperties;
}

export function Spotlight({
  open,
  onClose,
  items = [],
  placeholder = "Search…",
  className,
  style,
}: SpotlightProps) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = items.filter(
    (it) =>
      it.label.toLowerCase().includes(query.toLowerCase()) ||
      it.description?.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      filtered[active]?.action?.();
      onClose();
    } else if (e.key === "Escape") {
      onClose();
    }
  }

  if (!open) return null;

  return (
    <div className="spotlight-overlay" onClick={onClose}>
      <div
        className={cn("spotlight", className)}
        style={style}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
      >
        <div className="spotlight-input-row">
          <svg className="spotlight-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            className="spotlight-input"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd className="spotlight-esc">esc</kbd>
        </div>
        {filtered.length > 0 && (
          <ul className="spotlight-list">
            {filtered.map((item, i) => (
              <li
                key={item.id}
                className={cn("spotlight-item", i === active && "spotlight-item--active")}
                onMouseEnter={() => setActive(i)}
                onClick={() => { item.action?.(); onClose(); }}
              >
                {item.icon && <span className="spotlight-item-icon">{item.icon}</span>}
                <div className="spotlight-item-text">
                  <span className="spotlight-item-label">{item.label}</span>
                  {item.description && <span className="spotlight-item-desc">{item.description}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
        {query && filtered.length === 0 && (
          <div className="spotlight-empty">No results for "{query}"</div>
        )}
      </div>
    </div>
  );
}
